interface RequestOptions {
  url: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  data?: any;
  params?: any;
  headers?: any;
}

import { addBreadcrumb, reportError } from './monitor';

// 获取环境变量配置的 Base URL
// H5 环境下使用相对路径 /api/v1 以触发代理
// 非 H5 环境（App/小程序）使用绝对路径
const ENV_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://www.ccfca.top/api/v1';

let baseUrl = ENV_BASE_URL;

// #ifdef H5
baseUrl = '/api/v1';
// #endif

export const BASE_URL = baseUrl;

// 图片基础路径 (始终使用绝对路径，指向后端服务器)
// 解析出后端根域名 (例如 https://www.ccfca.top)
const API_ROOT = ENV_BASE_URL.replace(/\/api\/v1\/?$/, '');
export const IMG_BASE_URL = API_ROOT;

const request = (options: RequestOptions) => {
  return new Promise((resolve, reject) => {
    // 1. 处理 URL (拼接参数)
    let url = options.url;
    if (!url.startsWith('http')) {
      url = BASE_URL + url;
    }
    
    // --- 处理 Query Params (模拟 axios 的 params 行为) ---
    // 背景：
    // 1. uni.request 的 data 参数在 GET 请求时会自动拼接到 URL，但在 POST/PUT 时会作为 Body 发送。
    // 2. 某些后端接口（如 POST /auth/send-code）要求参数必须在 URL 中（Query Param），而不是 Body 中。
    // 3. PC 端使用的 axios 库会自动将 params 字段序列化为 URL 参数，为了保持 API 定义的一致性，这里手动模拟该行为。
    
    // 逻辑：
    // 如果存在 params 参数，且不是 GET 请求（GET 请求后续会由 uni.request 自动处理），
    // 则手动将 params 对象序列化为查询字符串并拼接到 URL 上。
    if (options.params && options.method !== 'GET') {
      const queryStr = Object.keys(options.params)
        .map(key => {
          const val = options.params[key];
          // 过滤掉 undefined 和 null 的值
          if (val === undefined || val === null) return '';
          // 对 key 和 value 进行 URI 编码，防止特殊字符破坏 URL 结构
          return `${encodeURIComponent(key)}=${encodeURIComponent(val)}`;
        })
        .filter(Boolean) // 过滤掉空字符串
        .join('&');
        
      if (queryStr) {
        // 判断原 URL 是否已包含参数，决定使用 ? 还是 & 连接
        url += (url.includes('?') ? '&' : '?') + queryStr;
      }
    }
    
    // 2. 获取 Token
    const token = uni.getStorageSync('customer_token');
    
    // 3. 构造 Headers
    const header = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    if (token) {
      header['Authorization'] = `Bearer ${token}`;
    }

    // 4. 发起请求
    // 确定传给 uni.request 的 data
    // 如果是 GET，data = params || data
    // 如果是 POST/PUT 等，data = data (params 已经拼接到 url)
    let requestData: any;
    if (options.method === 'GET') {
      requestData = options.params || options.data;
    } else {
      requestData = options.data;
    }

    // 记录 API 请求面包屑
    addBreadcrumb({
      type: 'api',
      message: `${options.method || 'GET'} ${url}`,
      data: {
        // 避免记录敏感数据，可根据需要过滤
        params: options.params, 
        // body 数据量大时可考虑截断
      }
    });

    uni.request({
      url: url,
      method: options.method || 'GET',
      data: requestData,
      header: header,
      timeout: 60000, // 设置超时时间为 60s
      success: (res: any) => {
        const { statusCode, data } = res;
        
        // HTTP 状态码错误
        if (statusCode !== 200) {
           if (statusCode === 401) {
             uni.removeStorageSync('customer_token');
             uni.showToast({ title: '登录已过期', icon: 'none' });
             setTimeout(() => {
               uni.reLaunch({ url: '/pages/auth/login' });
             }, 1500);
           } else {
             const errorMsg = data.detail || data.msg || `请求错误 ${statusCode}`;
             console.error(`Request Failed: ${url}`, res);
             uni.showToast({ title: errorMsg, icon: 'none' });
             
             // 上报接口错误
             reportError({
               error_type: 'api_http',
               error_msg: `HTTP ${statusCode}: ${errorMsg}`,
               device_info: { url, statusCode, response: data }
             });
           }
           reject(res);
           return;
        }

        // 业务状态码处理 (假设后端标准结构是 { code: 200, data: ... })
        // 根据 PC 端 request.ts，后端返回结构是 { code: number, data: any, msg: string }
        if (data && typeof data.code === 'number') {
          if (data.code === 200) {
            resolve(data.data);
          } else {
            uni.showToast({ title: data.msg || '系统错误', icon: 'none' });
            
            // 上报业务错误
            reportError({
               error_type: 'api_biz',
               error_msg: `Biz Error ${data.code}: ${data.msg}`,
               device_info: { url, code: data.code, response: data }
            });
            
            reject(new Error(data.msg || '系统错误'));
          }
        } else {
          // 非标准结构，直接返回 data
          resolve(data);
        }
      },
      fail: (err) => {
        console.error('Network Error:', err);
        uni.showToast({ title: err.errMsg || '网络请求失败', icon: 'none' });
        
        // 上报网络错误
        reportError({
          error_type: 'api_network',
          error_msg: `Network Error: ${err.errMsg}`,
          device_info: { url, error: err }
        });
        
        reject(err);
      }
    });
  });
};

export default request;
